import {
  mdiCalendarClockOutline,
  mdiCardAccountDetailsOutline,
  mdiCardAccountPhoneOutline,
  mdiRobotOutline,
  mdiShieldCheckOutline,
} from "@mdi/js"
import {
  buildViewNavigation,
  type PageTabWithId,
  type ViewPageSnippets,
  type ViewPageSpec,
} from "@kontextwerk/web-sdk/core/navigation/viewNavigation"
import type { OrgPresentationMode } from "./_shared/_logic/presentation-mode"

type OrgPresentationPageContext = {
  presentationMode: OrgPresentationMode
  hasContactMethodScope: boolean
}

export type OrgPresentationPaths = {
  profile: string
  assist: string
  business: string
  privacy: string
  contactMethods: string
  legacyContact: string
}

const PRESENTATION_WIDTH_TYPE = "small" as const
const APPOINTMENT_WIDTH_TYPE = "normal" as const

const normalizePresentationBasePath = (path: string): string => {
  let basePath = path.trim() || "/presentation"
  while (basePath.length > 1 && basePath.endsWith("/")) {
    basePath = basePath.slice(0, -1)
  }

  if (basePath.endsWith("/kontakt")) {
    basePath = basePath.slice(0, -"/kontakt".length)
  }

  if (basePath.endsWith("/betrieb")) {
    basePath = basePath.slice(0, -"/betrieb".length)
  }

  if (basePath.endsWith("/datenschutz")) {
    basePath = basePath.slice(0, -"/datenschutz".length)
  }

  if (basePath.endsWith("/kontaktmittel")) {
    basePath = basePath.slice(0, -"/kontaktmittel".length)
  }

  if (basePath.endsWith("/kehrassist")) {
    basePath = basePath.slice(0, -"/kehrassist".length)
  }

  return basePath || "/presentation"
}

export const buildOrgPresentationPaths = (presentationBasePath: string): OrgPresentationPaths => {
  const profile = normalizePresentationBasePath(presentationBasePath)

  return {
    profile,
    assist: `${profile}/kehrassist`,
    business: `${profile}/betrieb`,
    privacy: `${profile}/datenschutz`,
    contactMethods: `${profile}/kontaktmittel`,
    legacyContact: `${profile}/kontakt`,
  }
}

const getProfileTabs = (): PageTabWithId[] => [
  { id: "appointment", title: "Terminformular", widthType: APPOINTMENT_WIDTH_TYPE },
]

const getAssistTabs = (): PageTabWithId[] => [
  { id: "assist", title: "KehrAssist", widthType: PRESENTATION_WIDTH_TYPE },
]

const getBusinessTabs = (): PageTabWithId[] => [
  { id: "business", title: "Betrieb", widthType: PRESENTATION_WIDTH_TYPE },
]

const getPrivacyTabs = (): PageTabWithId[] => [
  { id: "privacy", title: "Datenschutz", widthType: PRESENTATION_WIDTH_TYPE },
]

const getContactMethodTabs = (): PageTabWithId[] => [
  { id: "contactMethods", title: "Kontaktmittel", widthType: PRESENTATION_WIDTH_TYPE },
]

const PAGE_SPECS: Array<ViewPageSpec<OrgPresentationPageContext, OrgPresentationPaths>> = [
  {
    id: "profile",
    title: "Terminseite",
    icon: mdiCalendarClockOutline,
    widthType: PRESENTATION_WIDTH_TYPE,
    resolvePath: (paths) => paths.profile,
    canOpen: (_capabilities, ctx) => ctx.presentationMode !== "bot_only",
    getTabs: getProfileTabs,
  },
  {
    id: "assist",
    title: "KehrAssist",
    icon: mdiRobotOutline,
    widthType: PRESENTATION_WIDTH_TYPE,
    resolvePath: (paths) => paths.assist,
    canOpen: (_capabilities, ctx) => ctx.presentationMode !== "appointment_portal_only",
    getTabs: getAssistTabs,
  },
  {
    id: "business",
    title: "Betrieb",
    icon: mdiCardAccountPhoneOutline,
    widthType: PRESENTATION_WIDTH_TYPE,
    resolvePath: (paths) => paths.business,
    getTabs: getBusinessTabs,
  },
  {
    id: "privacy",
    title: "Datenschutz",
    icon: mdiShieldCheckOutline,
    widthType: PRESENTATION_WIDTH_TYPE,
    resolvePath: (paths) => paths.privacy,
    getTabs: getPrivacyTabs,
  },
  {
    id: "contactMethods",
    title: "Kontaktmittel",
    icon: mdiCardAccountDetailsOutline,
    widthType: PRESENTATION_WIDTH_TYPE,
    resolvePath: (paths) => paths.contactMethods,
    canOpen: (_capabilities, ctx) => ctx.hasContactMethodScope,
    getTabs: getContactMethodTabs,
  },
]

export const buildOrgPresentationNavigation = ({
  context,
  paths,
  snippets,
}: {
  context: OrgPresentationPageContext
  paths: OrgPresentationPaths
  snippets: ViewPageSnippets
}) =>
  buildViewNavigation({
    specs: PAGE_SPECS,
    context,
    paths,
    capabilities: null,
    snippets,
  })
